import React, { useEffect, useRef, useState, useMemo } from "react";
import * as XLSX from "xlsx";
import "../styles/IncomeExpenseManager.css";
import MonthlySummaryReport from "./MonthlySummaryReport";
import DailyJournal from "./DailyJournal";
import VoucherDownload from "./VoucherDownload";
import ReceiptDownload from "./ReceiptDownload";
import API_URL from "../apiConfig";

/* ---------- Utility Functions ---------- */

function getFinYearList(start = 2000, n = 50) {
  const arr = [];
  for (let y = start; y < start + n; ++y) arr.push(`${y}-${y + 1}`);
  return arr;
}
function finYearToDates(fy) {
  if (!fy || !fy.includes("-")) return { start: "", end: "" };
  const [y1, y2] = fy.split("-");
  return { start: `${y1}-04-01`, end: `${y2}-03-31` };
}

/**
 * toComparableDate
 * - Accepts dd/mm/yyyy, dd-mm-yyyy, yyyy-mm-dd, or JS Date-like strings
 * - Returns yyyy-mm-dd (suitable for string comparison) or "" if invalid
 */
function toComparableDate(dateStr) {
  if (!dateStr) return "";
  const s = String(dateStr).trim();
  // dd/mm/yyyy or dd-mm-yyyy
  const dmSlash = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const dmDash = /^(\d{2})-(\d{2})-(\d{4})$/;
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/;
  let m;
  if ((m = s.match(dmSlash))) {
    const [, d, mo, y] = m;
    return `${y}-${mo}-${d}`;
  }
  if ((m = s.match(dmDash))) {
    const [, d, mo, y] = m;
    return `${y}-${mo}-${d}`;
  }
  if ((m = s.match(ymd))) {
    return `${m[1]}-${m[2]}-${m[3]}`;
  }
  // Fallback to Date parsing (best effort)
  const dObj = new Date(s);
  if (!isNaN(dObj)) return dObj.toISOString().slice(0, 10);
  return "";
}
function safeParseDateToTs(dateStr) {
  const dStr = toComparableDate(dateStr);
  if (!dStr) return 0;
  const t = Date.parse(dStr);
  return isNaN(t) ? 0 : t;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const s = String(dateStr).trim();
  const ymd = /^\d{4}-\d{2}-\d{2}$/;
  const dmyDash = /^\d{2}-\d{2}-\d{4}$/;
  const dmySlash = /^\d{2}\/\d{2}\/\d{4}$/;
  if (ymd.test(s)) {
    const [y, m, d] = s.split("-");
    return `${d}-${m}-${y}`;
  }
  if (dmyDash.test(s)) return s;
  if (dmySlash.test(s)) return s.replace(/\//g, "-");
  const d = new Date(s);
  if (isNaN(d)) return s;
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
}

function formatCurrency(num) {
  const n = Number(num || 0);
  if (!isFinite(n)) return "₹0.00";
  return "₹" + n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function parseAmountStringToNumber(str) {
  if (str === "" || str === null || str === undefined) return 0;
  const n = parseFloat(String(str).replace(/,/g, ""));
  return Number.isFinite(n) ? parseFloat(n.toFixed(2)) : 0;
}

/* ---------- Business helpers ---------- */

function computeBalances(incomes = [], expenses = [], transfers = [], openingCash = 0, openingBank = 0) {
  let cash = parseAmountStringToNumber(openingCash);
  let bank = parseAmountStringToNumber(openingBank);

  (incomes || []).forEach(row => {
    const mode = String(row.mode || "").toLowerCase();
    const amt = parseAmountStringToNumber(row.amount ?? row.amount_paid ?? 0);
    if (["bank", "online", "upi", "scholarship", "carryforward"].includes(mode)) bank += amt;
    else cash += amt;
  });

  (expenses || []).forEach(row => {
    const mode = String(row.mode || "").toLowerCase();
    const amt = parseAmountStringToNumber(row.amount ?? 0);
    if (["bank", "online", "upi", "scholarship"].includes(mode)) bank -= amt;
    else cash -= amt;
  });

  (transfers || []).forEach(t => {
    const amt = parseAmountStringToNumber(t.amount ?? 0);
    const from = String(t.from_account || "").toLowerCase();
    const to = String(t.to_account || "").toLowerCase();
    if (from === "cash") cash -= amt;
    else if (from === "bank") bank -= amt;
    if (to === "cash") cash += amt;
    else if (to === "bank") bank += amt;
  });

  return { cash, bank };
}

/* ---------- Refund detection & particulars formatting ---------- */
function isRefundRow(row = {}) {
  const acc = String(row.account_head || "").toLowerCase();
  const rno = String(row.receipt_no || "").toUpperCase();
  const amt = Number(row.amount ?? row.amount_paid ?? 0);
  if (acc.includes("refund")) return true;
  if (rno.startsWith("RE")) return true;
  if (Number.isFinite(amt) && amt < 0) return true;
  return false;
}

function getCarryAmount(row = {}) {
  const candidates = [
    "carry_amount",
    "carryforward_amount",
    "carryforward",
    "carry",
    "amount",
    "amount_paid",
    "original_amount",
    "fee_amount",
    "paid_amount",
    "scholarship_amount",
    "refunded_amount",
  ];
  for (const f of candidates) {
    if (row[f] !== undefined && row[f] !== null && String(row[f]).trim() !== "") {
      const n = parseAmountStringToNumber(row[f]);
      if (Number.isFinite(n) && Math.abs(n) > 0) return Math.abs(n);
    }
  }

  if (row.particulars && typeof row.particulars === "string") {
    const m = row.particulars.replace(/,/g, "").match(/(\d+(?:\.\d+)?)/);
    if (m) {
      const n = parseFloat(m[1]);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }

  return 0;
}

function getParticularsDisplay(row = {}) {
  if (row.particulars && String(row.particulars).trim()) return String(row.particulars);
  if (row.reference && String(row.reference).trim()) return String(row.reference);

  const mode = String(row.mode || "").toLowerCase();
  if (mode.includes("carryforward")) {
    const amount = getCarryAmount(row);
    let baseLabel = "Carryforward from previous year";
    if (row.reference && String(row.reference).trim()) baseLabel = String(row.reference);
    if (row.course_year) {
      const fromYear = Number(row.course_year) - 1;
      if (Number.isFinite(fromYear)) baseLabel = `Carryforward from Year ${fromYear}`;
    }
    if (amount && Number.isFinite(amount) && Math.abs(amount) > 0) {
      return `${baseLabel} — ${formatCurrency(amount)}`;
    }
    return baseLabel;
  }

  if (isRefundRow(row)) {
    const student = row.student_name ? ` to ${row.student_name}` : "";
    const rn = row.receipt_no || row.voucher_no || row.id || "";
    const amount = getCarryAmount(row) || Math.abs(Number(row.amount ?? row.amount_paid ?? 0));
    if (amount && Number.isFinite(amount) && Math.abs(amount) > 0) {
      return `Fee Refund${student}${rn ? ` (${rn})` : ""} — ${formatCurrency(Math.abs(amount))}`;
    }
    return `Fee Refund${student}${rn ? ` (${rn})` : ""}`;
  }

  return "-";
}

/* ---------- Deduplication helpers ---------- */

function filterOutTransferDuplicates(incomes = [], expenses = [], transfers = []) {
  const voucherSet = new Set();
  (transfers || []).forEach(t => {
    if (t.voucher_no) voucherSet.add(String(t.voucher_no).toUpperCase());
    if (t.id) voucherSet.add(String(t.id).toUpperCase());
    if (t.reference) voucherSet.add(String(t.reference).toUpperCase());
  });

  const isTransferRow = (r) => {
    const v = String(r.voucher_no || r.receipt_no || r.reference || r.id || "").toUpperCase();
    return v && voucherSet.has(v);
  };

  const filteredIncome = (incomes || []).filter(r => !isTransferRow(r));
  const filteredExpense = (expenses || []).filter(r => !isTransferRow(r));

  return { filteredIncome, filteredExpense };
}

/* ---------- Component ---------- */

export default function IncomeExpenseManager({ token }) {
  // Base URL resolution: prefer API_URL (apiConfig), fallback to env (helpful for production)
  const BASE_URL = API_URL || process.env.REACT_APP_API_URL || "";

  const finYearList = getFinYearList(2000, 50);
  const today = new Date();
  const currYear = today.getMonth() < 3 ? today.getFullYear() - 1 : today.getFullYear();
  const currFinYear = `${currYear}-${currYear + 1}`;
  const [finYear, setFinYear] = useState(currFinYear);

  const [tab, setTab] = useState("income");
  const [opening, setOpening] = useState({ cash: 0, bank: 0 });
  const [editOB, setEditOB] = useState(false);
  const [obInput, setObInput] = useState({ cash: "", bank: "" });

  const [income, setIncome] = useState([]);
  const [expense, setExpense] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [balance, setBalance] = useState({ cash_balance: 0, bank_balance: 0 });

  const [search, setSearch] = useState({
    receipt_no: "",
    date: "",
    account_head: "",
    particulars: "",
    reference: "",
    amount: "",
    mode: "",
    student_name: "",
    roll_no: "",
    batch_id: "",
    course_year: "",
    session_year: "",
  });

  const [form, setForm] = useState({
    receipt_no: "",
    date: "",
    account_head: "",
    particulars: "",
    reference: "",
    amount: "",
    mode: "cash",
    voucher_no: "",
    student_name: "",
    roll_no: "",
    batch_id: "",
    course_year: "",
    session_year: "",
    from_account: "cash",
    to_account: "bank",
  });

  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [transferEditId, setTransferEditId] = useState(null);

  const [apiError, setApiError] = useState("");

  const uploadRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [uploadDetails, setUploadDetails] = useState([]);

  /* ---------- Data fetchers (return data) ---------- */
  const fetchIncome = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/income_expense/income?finyear=${finYear}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Unable to load income");
      let data = await res.json().catch(() => []);
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error("fetchIncome error:", err);
      return [];
    }
  };
  const fetchExpense = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/income_expense/expense?finyear=${finYear}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Unable to load expenses");
      const data = await res.json().catch(() => []);
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error("fetchExpense error:", err);
      return [];
    }
  };
  const fetchTransfers = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/income_expense/transfers?finyear=${finYear}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Unable to load transfers");
      const data = await res.json().catch(() => []);
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error("fetchTransfers error:", err);
      return [];
    }
  };

  // central loader: fetch all 3, sanitize duplicates (transfer voucher duplication), then set states
  const loadAllData = async () => {
    setApiError("");
    try {
      const [inc, exp, trf] = await Promise.all([fetchIncome(), fetchExpense(), fetchTransfers()]);

      // Remove income/expense rows that look like transfers (by voucher_no/receipt/ref matching)
      const { filteredIncome, filteredExpense } = filterOutTransferDuplicates(inc, exp, trf);

      setIncome(filteredIncome);
      setExpense(filteredExpense);
      setTransfers(trf);

      // fetch opening & balance
      try {
        const obRes = await fetch(`${BASE_URL}/api/income_expense/summary/opening_balance?finyear=${finYear}`, { headers: { Authorization: `Bearer ${token}` } });
        const ob = await obRes.json().catch(()=>({}));
        setOpening({ cash: parseAmountStringToNumber(ob.cash_opening ?? 0), bank: parseAmountStringToNumber(ob.bank_opening ?? 0) });
        setObInput({ cash: ob.cash_opening !== undefined && ob.cash_opening !== null ? String(ob.cash_opening) : "", bank: ob.bank_opening !== undefined && ob.bank_opening !== null ? String(ob.bank_opening) : "" });
      } catch (err) {
        setOpening({ cash:0, bank:0});
        setObInput({cash:"", bank:""});
      }

      try {
        const balRes = await fetch(`${BASE_URL}/api/income_expense/summary/balance?finyear=${finYear}`, { headers: { Authorization: `Bearer ${token}` } });
        const bal = await balRes.json().catch(()=>({}));
        setBalance({ cash_balance: parseAmountStringToNumber(bal.cash_balance ?? 0), bank_balance: parseAmountStringToNumber(bal.bank_balance ?? 0) });
      } catch {
        setBalance({cash_balance:0, bank_balance:0});
      }

    } catch (err) {
      console.error("loadAllData error:", err);
      setIncome([]);
      setExpense([]);
      setTransfers([]);
    }
  };

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line
  }, [token, finYear, tab]);

  const refreshAll = async () => { await loadAllData(); };

  /* ---------- Opening balance save ---------- */
  const saveOB = async () => {
    const payload = {
      finyear: finYear,
      cash_opening: parseAmountStringToNumber(obInput.cash),
      bank_opening: parseAmountStringToNumber(obInput.bank)
    };
    await fetch(`${BASE_URL}/api/income_expense/summary/opening_balance`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    setOpening({ cash: payload.cash_opening, bank: payload.bank_opening });
    setEditOB(false);
    await refreshAll();
  };

  /* ---------- Add / Edit / Delete handlers ---------- */
  const addEntry = async (type) => {
    setApiError("");
    if (type === "transfer") {
      if (!form.amount || Number.parseFloat(form.amount) <= 0) {
        setApiError("Please enter a valid transfer amount.");
        return;
      }
      if (!form.date) {
        setApiError("Please enter a transfer date.");
        return;
      }
      const from = String(form.from_account || "cash").toLowerCase();
      const to = String(form.to_account || "bank").toLowerCase();
      if (from === to) {
        setApiError("From and To accounts must differ.");
        return;
      }

      const payload = {
        date: form.date || new Date().toISOString().slice(0,10),
        amount: parseAmountStringToNumber(form.amount),
        from_account: from,
        to_account: to,
        particulars: form.particulars || "",
        reference: form.reference || "",
        finyear: finYear,
        voucher_no: form.voucher_no || null
      };

      try {
        if (transferEditId) {
          const res = await fetch(`${BASE_URL}/api/income_expense/transfers/${transferEditId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(payload)
          });
          const body = await res.json().catch(()=>({}));
          if (!res.ok) {
            setApiError(body.error || body.details || `Server returned ${res.status}`);
            return;
          }
          setTransferEditId(null);
        } else {
          const res = await fetch(`${BASE_URL}/api/income_expense/transfers`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(payload)
          });
          const body = await res.json().catch(()=>({}));
          if (!res.ok) {
            setApiError(body.error || body.details || `Server returned ${res.status}`);
            return;
          }
        }

        setForm(f => ({ ...f, amount: "", date: "", particulars: "", reference: "", from_account: "cash", to_account: "bank", voucher_no: "" }));
        await refreshAll();
      } catch (err) {
        console.error("Failed to add/update transfer", err);
        setApiError("Failed to save transfer. See console for details.");
      }
      return;
    }

    if (!form.date || !form.account_head || !form.mode) {
      setApiError("Please enter Date, Account Head and Mode before adding.");
      return;
    }
    const parsedAmount = parseAmountStringToNumber(form.amount);
    if (!Number.isFinite(parsedAmount)) {
      setApiError("Please enter a valid numeric amount.");
      return;
    }

    const url = `${BASE_URL}/api/income_expense/${type}`;
    let body = { ...form, finyear: finYear, amount: parsedAmount };
    if (type === "income" && !body.receipt_no && !String(body.mode || "").toLowerCase().includes("carryforward")) {
      body.receipt_no = "RCPT" + Date.now();
    }
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const resBody = await res.json().catch(()=>({}));
      if (!res.ok) {
        setApiError(resBody.error || resBody.details || `Server returned ${res.status}`);
        return;
      }

      setForm({
        receipt_no: "",
        date: "",
        account_head: "",
        particulars: "",
        reference: "",
        amount: "",
        mode: "cash",
        voucher_no: "",
        student_name: "",
        roll_no: "",
        batch_id: "",
        course_year: "",
        session_year: "",
        from_account: "cash",
        to_account: "bank",
      });
      setApiError("");
      await refreshAll();
    } catch (err) {
      console.error("Failed to add entry", err);
      setApiError("Failed to save entry. See console for details.");
    }
  };

  const deleteEntry = async (type, id) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;
    let url;
    if (type === "transfer") url = `${BASE_URL}/api/income_expense/transfers/${id}`;
    else url = `${BASE_URL}/api/income_expense/${type}/${id}`;
    await fetch(url, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` }
    });
    await refreshAll();
  };

  const startEdit = (row) => {
    setTransferEditId(null);
    setEditId(row.id);
    setEditForm({
      receipt_no: row.receipt_no || "",
      voucher_no: row.voucher_no || "",
      date: row.date ? toComparableDate(row.date) : "",
      account_head: row.account_head || "",
      particulars: row.particulars || "",
      reference: row.reference || "",
      amount: row.amount !== undefined && row.amount !== null ? String(parseAmountStringToNumber(row.amount).toFixed(2)) : "",
      mode: row.mode || "cash",
      student_name: row.student_name || "",
      roll_no: row.roll_no || "",
      batch_id: row.batch_id || "",
      course_year: row.course_year || "",
      session_year: row.session_year || ""
    });
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditForm({});
  };

  const saveEdit = async (row) => {
    const url = `${BASE_URL}/api/income_expense/${tab}/${row.id}`;
    const body = { ...editForm, finyear: finYear };
    body.amount = parseAmountStringToNumber(editForm.amount);
    await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body)
    });
    setEditId(null);
    setEditForm({});
    await refreshAll();
  };

  const startTransferEdit = (t) => {
    setEditId(null);
    setTransferEditId(t.id);
    setForm(f => ({
      ...f,
      date: toComparableDate(t.date) || "",
      amount: t.amount !== undefined && t.amount !== null ? String(parseAmountStringToNumber(t.amount).toFixed(2)) : "",
      from_account: t.from_account || "cash",
      to_account: t.to_account || "bank",
      particulars: t.particulars || "",
      reference: t.reference || "",
      voucher_no: t.voucher_no || ""
    }));
    setTab("transfer");
  };

  const cancelTransferEdit = () => {
    setTransferEditId(null);
    setForm(f => ({ ...f, amount: "", date: "", particulars: "", reference: "", from_account: "cash", to_account: "bank", voucher_no: "" }));
    setApiError("");
  };

  /* ---------- File / Excel helpers ---------- */

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0] || null);
    setUploadMsg("");
    setUploadDetails([]);
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setUploadMsg("");
    setUploadDetails([]);
    const formData = new FormData();
    formData.append("file", selectedFile);
    try {
      const res = await fetch(`${BASE_URL}/api/income_expense/bulk_upload/${tab}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        setUploadMsg(`✅ Upload successful! Imported ${data.count} rows.`);
        setSelectedFile(null);
        if (uploadRef.current) uploadRef.current.value = "";
        await refreshAll();
      } else {
        const err = await res.json().catch(() => ({}));
        setUploadMsg("❌ Upload failed: " + (err.error || "Unknown error"));
        setUploadDetails(err.details || []);
      }
    } catch (err) {
      setUploadMsg("❌ Upload failed. Try again.");
    }
    setUploading(false);
  };

  const handleDownloadExcel = async () => {
    const url = `${BASE_URL}/api/income_expense/download/${tab}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${tab}_${finYear}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleClientDownload = async () => {
    const data = filterRowsByFYAndSearch(tab === "income" ? income : expense)
      .map(r => ({ ...r, amount: parseAmountStringToNumber(r.amount).toFixed(2) }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tab);
    XLSX.writeFile(wb, `${tab}_${finYear}_records.xlsx`);
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearch(prev => ({ ...prev, [name]: value }));
  };

  /* ---------- Derived rows & filters (sorting ascending oldest->newest) ---------- */

  const combinedExpenseRows = useMemo(() => {
    const refundRows = (income || [])
      .filter(r => isRefundRow(r))
      .map(r => ({
        ...r,
        amount: Math.abs(Number(r.amount ?? r.amount_paid ?? 0)),
        account_head: r.account_head || "Fee Refund",
        particulars: r.particulars && String(r.particulars).trim() ? r.particulars : (`Refund to ${r.student_name || "-"} (${r.receipt_no || r.id || "-"})`),
        _source: "income"
      }));
    // include original expense rows + derived refund rows
    return [...(expense || []), ...refundRows];
  }, [income, expense]);

  const filterRowsByFYAndSearch = (rows) => {
    const { start, end } = finYearToDates(finYear);
    return (rows || [])
      .filter(row => {
        const d = toComparableDate(row.date || row.payment_date || row.date_of);
        if (start && d && d < start) return false;
        if (end && d && d > end) return false;
        for (const key in search) {
          const val = String(search[key] || "").trim().toLowerCase();
          if (val) {
            const fieldVal = String(row[key] || row.particulars || row.reference || row.account_head || row.student_name || row.receipt_no || row.voucher_no || "");
            if (fieldVal.toLowerCase().indexOf(val) === -1) return false;
          }
        }
        return true;
      })
      .sort((a, b) => {
        const ta = safeParseDateToTs(a.date || a.payment_date || a.created_at || "");
        const tb = safeParseDateToTs(b.date || b.payment_date || b.created_at || "");
        if (ta !== tb) return ta - tb; // ascending: oldest first
        // fallback stable ordering: compare id or receipt/voucher
        const ida = String(a.id || a.receipt_no || a.voucher_no || "");
        const idb = String(b.id || b.receipt_no || b.voucher_no || "");
        return ida.localeCompare(idb);
      });
  };

  const filterTransfersByFYAndSearch = (rows) => {
    const { start, end } = finYearToDates(finYear);
    return (rows || [])
      .filter(row => {
        const d = toComparableDate(row.date);
        if (start && d && d < start) return false;
        if (end && d && d > end) return false;
        const val = String(search.particulars || search.reference || "").trim().toLowerCase();
        if (val) {
          const hay = `${row.particulars || ""} ${row.reference || ""} ${row.voucher_no || ""}`.toLowerCase();
          if (hay.indexOf(val) === -1) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const ta = safeParseDateToTs(a.date);
        const tb = safeParseDateToTs(b.date);
        if (ta !== tb) return ta - tb;
        return String(a.voucher_no || a.id || "").localeCompare(String(b.voucher_no || b.id || ""));
      });
  };

  const combinedClosing = (() => {
    const fInc = filterRowsByFYAndSearch(income.filter(r => !isRefundRow(r)));
    const fExp = filterRowsByFYAndSearch(combinedExpenseRows);
    const fTrf = filterTransfersByFYAndSearch(transfers);
    return computeBalances(fInc, fExp, fTrf, opening.cash, opening.bank);
  })();

  /* ---------- PDF helpers ---------- */

  const openReceiptPDF = async (row) => {
    try {
      const student = {
        name: row.student_name || undefined,
        batch: row.batch_name || row.batch_id || undefined,
        course_year: row.course_year || undefined,
      };
      await ReceiptDownload.generatePDF(row, { student });
    } catch (err) {
      console.error("Failed to generate receipt PDF", err);
      alert("Failed to generate receipt PDF. See console for details.");
    }
  };

  const openVoucherPDF = async (row) => {
    try {
      await VoucherDownload.generatePDF(row);
    } catch (err) {
      console.error("Failed to generate voucher PDF", err);
      alert("Failed to generate voucher PDF. See console for details.");
    }
  };

  /* ---------- Render UI ---------- */
  return (
    <div className="iem-container">
      <h2>Income & Expense Manager</h2>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <b>Financial Year:</b>
        <select
          value={finYear}
          onChange={e => setFinYear(e.target.value)}
          style={{ padding: "4px 14px", borderRadius: 4, border: "1px solid #ccc", minWidth: 130, fontSize: 15 }}
        >
          {finYearList.map(fy => <option key={fy} value={fy}>{fy}</option>)}
        </select>
      </div>

      <div className="iem-tabs">
        <button onClick={() => setTab("income")} className={tab === "income" ? "active" : ""}>Income</button>
        <button onClick={() => setTab("expense")} className={tab === "expense" ? "active" : ""}>Expense</button>
        <button onClick={() => setTab("transfer")} className={tab === "transfer" ? "active" : ""}>Transfer</button>
        <button onClick={() => setTab("dailyJournal")} className={tab === "dailyJournal" ? "active" : ""}>Daily Journal</button>
        <button onClick={() => setTab("monthlySummary")} className={tab === "monthlySummary" ? "active" : ""}>Monthly Summary</button>
        <button disabled style={{ float: "right" }}>
          Cash: {formatCurrency(balance.cash_balance)} | Bank: {formatCurrency(balance.bank_balance)}
        </button>
      </div>

      {(tab === "income" || tab === "expense" || tab === "transfer") && (
        <>
          <div style={{display: "flex", gap: 20, alignItems: "center", marginBottom: 8}}>
            <b>Opening Cash Balance:</b>
            {editOB
              ? <input value={obInput.cash} type="number" step="0.01" min="0" onChange={e=>setObInput(ob=>({...ob, cash:e.target.value}))} style={{width:120}} />
              : <span>{formatCurrency(opening.cash)}</span>
            }
            <b>Opening Bank Balance:</b>
            {editOB
              ? <input value={obInput.bank} type="number" step="0.01" min="0" onChange={e=>setObInput(ob=>({...ob,bank:e.target.value}))} style={{width:120}} />
              : <span>{formatCurrency(opening.bank)}</span>
            }
            {!editOB && <button onClick={()=>setEditOB(true)}>Edit</button>}
            {editOB && <button onClick={saveOB}>Save</button>}
            <div style={{marginLeft: "auto", fontWeight: "600"}}>
              Combined Closing: Cash {formatCurrency(combinedClosing.cash)} | Bank: {formatCurrency(combinedClosing.bank)}
            </div>
          </div>

          {apiError && <div style={{ color: "red", marginBottom: 8 }}>{apiError}</div>}

          {tab === "transfer" ? (
            <div style={{ marginBottom: 12, border: "1px solid #e6e6e6", padding: 12, borderRadius: 6 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <label>Date: <input type="date" value={form.date} onChange={e=>setForm(f=>({...f, date: e.target.value}))} /></label>
                <label>Amount: <input type="number" step="0.01" min="0" value={form.amount} onChange={e=>setForm(f=>({...f, amount: e.target.value}))} /></label>
                <label>From:
                  <select value={form.from_account} onChange={e=>setForm(f=>({...f, from_account: e.target.value}))}>
                    <option value="cash">Cash</option>
                    <option value="bank">Bank</option>
                  </select>
                </label>
                <label>To:
                  <select value={form.to_account} onChange={e=>setForm(f=>({...f, to_account: e.target.value}))}>
                    <option value="bank">Bank</option>
                    <option value="cash">Cash</option>
                  </select>
                </label>
                <input placeholder="Particulars" value={form.particulars} onChange={e=>setForm(f=>({...f, particulars: e.target.value}))} style={{width:240}} />
                <input placeholder="Reference/UTR" value={form.reference} onChange={e=>setForm(f=>({...f, reference: e.target.value}))} style={{width:160}} />
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="student-btn" style={{minWidth:140}} onClick={()=>addEntry("transfer")}>
                    {transferEditId ? "Save Transfer" : "Add Transfer"}
                  </button>
                  {transferEditId && (
                    <button className="student-btn" style={{minWidth:80}} onClick={cancelTransferEdit}>Cancel</button>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f3f3f3" }}>
                      <th style={{textAlign:"left", padding:8}}>Date</th>
                      <th style={{textAlign:"left", padding:8}}>Voucher</th>
                      <th style={{textAlign:"left", padding:8}}>Particulars</th>
                      <th style={{textAlign:"right", padding:8}}>Amount</th>
                      <th style={{textAlign:"left", padding:8}}>From</th>
                      <th style={{textAlign:"left", padding:8}}>To</th>
                      <th style={{textAlign:"center", padding:8}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterTransfersByFYAndSearch(transfers).map(t => (
                      <tr key={t.id}>
                        <td style={{padding:8}}>{formatDate(t.date)}</td>
                        <td style={{padding:8}}>{t.voucher_no || t.id}</td>
                        <td style={{padding:8}}>{t.particulars || t.reference || "-"}</td>
                        <td style={{padding:8, textAlign:"right"}}>{formatCurrency(t.amount)}</td>
                        <td style={{padding:8}}>{String(t.from_account || "").toUpperCase()}</td>
                        <td style={{padding:8}}>{String(t.to_account || "").toUpperCase()}</td>
                        <td style={{padding:8, textAlign:"center"}}>
                          <button style={{marginRight:8}} onClick={()=>startTransferEdit(t)}>Edit</button>
                          <button style={{marginRight:8}} onClick={()=>deleteEntry("transfer", t.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                    {transfers.length === 0 && (
                      <tr><td colSpan={7} style={{padding:12, textAlign:"center"}}>No transfers found for selected FY.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <>
              <div className="iem-search-row" style={{display:"flex",gap:6,marginBottom:8}}>
                <input placeholder="Receipt No" name="receipt_no" value={search.receipt_no} onChange={handleSearchChange} style={{width:90}}/>
                <input placeholder="dd/mm/yyyy or dd-mm-yyyy" name="date" value={search.date} onChange={handleSearchChange} style={{width:100}}/>
                <input placeholder="Account Head" name="account_head" value={search.account_head} onChange={handleSearchChange} style={{width:100}}/>
                <input placeholder="Particulars" name="particulars" value={search.particulars} onChange={handleSearchChange} style={{width:120}}/>
                <input placeholder="Reference" name="reference" value={search.reference} onChange={handleSearchChange} style={{width:80}}/>
                <input placeholder="Amount" name="amount" value={search.amount} onChange={handleSearchChange} style={{width:70}}/>
                <select name="mode" value={search.mode} onChange={handleSearchChange} style={{width:70}}>
                  <option value="">Mode</option>
                  <option value="cash">Cash</option>
                  <option value="bank">Bank</option>
                  <option value="online">Online</option>
                  <option value="upi">UPI</option>
                  <option value="scholarship">Scholarship</option>
                  <option value="Carryforward">Carryforward</option>
                </select>
                <input placeholder="Student Name" name="student_name" value={search.student_name} onChange={handleSearchChange} style={{width:110}}/>
                <input placeholder="Roll No" name="roll_no" value={search.roll_no} onChange={handleSearchChange} style={{width:70}}/>
                <input placeholder="Batch" name="batch_id" value={search.batch_id} onChange={handleSearchChange} style={{width:70}}/>
                <input placeholder="Class" name="course_year" value={search.course_year} onChange={handleSearchChange} style={{width:70}}/>
                <input placeholder="Session" name="session_year" value={search.session_year} onChange={handleSearchChange} style={{width:70}}/>
                <button className="student-btn" style={{minWidth:80}} onClick={()=>addEntry(tab)}>Add {tab.charAt(0).toUpperCase()+tab.slice(1)}</button>
              </div>

              <div className="iem-upload-section">
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  ref={uploadRef}
                  onChange={handleFileChange}
                  style={{ marginTop: 8, marginRight: 8 }}
                />
                <button
                  type="button"
                  onClick={handleFileUpload}
                  disabled={!selectedFile || uploading}
                  style={{ marginRight: 8 }}
                >
                  {uploading ? "Uploading..." : "Upload"}
                </button>
                <button type="button" onClick={handleDownloadExcel} style={{ marginRight: 8 }}>
                  Download All ({tab.charAt(0).toUpperCase() + tab.slice(1)}) Excel
                </button>
                <button type="button" onClick={handleClientDownload}>
                  Download Table View (Excel)
                </button>
                {uploadMsg && (
                  <div style={{ color: uploadMsg.startsWith("✅") ? "green" : "red", marginTop: 2 }}>{uploadMsg}</div>
                )}
                {uploadDetails.length > 0 && (
                  <div style={{ color: "red", margin: "8px 0", fontSize: 13 }}>
                    <div>Details:</div>
                    <ul>
                      {uploadDetails.slice(0, 10).map((err, i) => <li key={i}>{err}</li>)}
                      {uploadDetails.length > 10 && <li>...and {uploadDetails.length - 10} more.</li>}
                    </ul>
                  </div>
                )}
              </div>

              <div className="iem-table">
                <table>
                  <thead>
                    <tr>
                      <th>{tab === "income" ? "Receipt No" : "Voucher No"}</th>
                      <th>Date</th>
                      <th>Account</th>
                      <th>Particulars</th>
                      <th>Reference</th>
                      <th>Amount</th>
                      <th>Mode</th>
                      <th>Student Name</th>
                      <th>Roll No</th>
                      <th>Batch</th>
                      <th>Class</th>
                      <th>Session</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{fontWeight:"bold", background:"#f5f5f5"}}>
                      <td colSpan={5}>Opening Balance</td>
                      <td colSpan={3}>{formatCurrency(opening.cash)} | {formatCurrency(opening.bank)}</td>
                      <td colSpan={5}></td>
                    </tr>

                    {(() => {
                      const displayed = tab === "income"
                        ? filterRowsByFYAndSearch((income || []).filter(r => !isRefundRow(r)))
                        : filterRowsByFYAndSearch(combinedExpenseRows);

                      if (displayed.length === 0) {
                        return <tr><td colSpan={13} style={{padding:12, textAlign:"center"}}>No records found for selected FY.</td></tr>;
                      }

                      return displayed.map((row) =>
                        editId === row.id ? (
                          <tr key={row.id} style={{ background: "#f6f7f9" }}>
                            <td>
                              {tab === "income" ? (
                                <input value={editForm.receipt_no} onChange={e => setEditForm(f => ({ ...f, receipt_no: e.target.value }))} />
                              ) : (
                                <input value={editForm.voucher_no} onChange={e => setEditForm(f => ({ ...f, voucher_no: e.target.value }))} />
                              )}
                            </td>
                            <td>
                              <input type="date" value={toComparableDate(editForm.date)} onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} />
                            </td>
                            <td>
                              <input value={editForm.account_head} onChange={e => setEditForm(f => ({ ...f, account_head: e.target.value }))} />
                            </td>
                            <td>
                              <input value={editForm.particulars} onChange={e => setEditForm(f => ({ ...f, particulars: e.target.value }))} />
                            </td>
                            <td>
                              <input value={editForm.reference} onChange={e => setEditForm(f => ({ ...f, reference: e.target.value }))} />
                            </td>
                            <td>
                              <input type="number" step="0.01" min="0" value={editForm.amount} onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))} />
                            </td>
                            <td>
                              <select value={editForm.mode} onChange={e => setEditForm(f => ({ ...f, mode: e.target.value }))}>
                                <option value="cash">Cash</option>
                                <option value="bank">Bank</option>
                                <option value="online">Online</option>
                                <option value="UPI">UPI</option>
                                {tab === "income" && <option value="scholarship">Scholarship</option>}
                                {tab === "income" && <option value="Carryforward">Carryforward</option>}
                              </select>
                            </td>
                            <td>
                              <input value={editForm.student_name} onChange={e => setEditForm(f => ({ ...f, student_name: e.target.value }))} />
                            </td>
                            <td><input value={editForm.roll_no} onChange={e => setEditForm(f => ({ ...f, roll_no: e.target.value }))} /></td>
                            <td><input value={editForm.batch_id} onChange={e => setEditForm(f => ({ ...f, batch_id: e.target.value }))} /></td>
                            <td><input value={editForm.course_year} onChange={e => setEditForm(f => ({ ...f, course_year: e.target.value }))} /></td>
                            <td><input value={editForm.session_year} onChange={e => setEditForm(f => ({ ...f, session_year: e.target.value }))} /></td>
                            <td>
                              <button onClick={() => saveEdit(row)}>Save</button>
                              <button onClick={cancelEdit}>Cancel</button>
                            </td>
                          </tr>
                        ) : (
                          <tr key={row.id}>
                            <td>{tab === "income" ? (row.receipt_no || "-") : (row.voucher_no || (row.receipt_no || "-"))}</td>
                            <td>{formatDate(row.date || row.payment_date)}</td>
                            <td>{row.account_head}</td>
                            <td>{getParticularsDisplay(row)}</td>
                            <td>{row.reference}</td>
                            <td>{formatCurrency(parseAmountStringToNumber(row.amount ?? row.amount_paid ?? 0))}</td>
                            <td>{row.mode}</td>
                            <td>{row.student_name || "-"}</td>
                            <td>{row.roll_no || "-"}</td>
                            <td>{row.batch_id || row.batch_name || "-"}</td>
                            <td>{row.course_year || "-"}</td>
                            <td>{row.session_year || "-"}</td>
                            <td>
                              <button onClick={() => startEdit(row)}>Edit</button>
                              <button onClick={() => {
                                if (row._source === "income") deleteEntry("income", row.id);
                                else deleteEntry(tab === "income" ? "income" : "expense", row.id);
                              }}>Delete</button>

                              { (tab === "income") && (
                                <button onClick={() => openReceiptPDF(row)}>PDF</button>
                              )}
                              { (tab === "expense") && (
                                row._source === "income"
                                  ? <button onClick={() => openReceiptPDF(row)}>Receipt (Refund)</button>
                                  : <button onClick={() => openVoucherPDF(row)}>Voucher</button>
                              )}
                            </td>
                          </tr>
                        )
                      );
                    })()}

                    <tr style={{fontWeight:"bold", background:"#f5f5f5"}}>
                      <td colSpan={5}>Closing Balance</td>
                      <td colSpan={3}>
                        {
                          (()=>{ 
                            const fInc = filterRowsByFYAndSearch(income.filter(r => !isRefundRow(r)));
                            const fExp = filterRowsByFYAndSearch(combinedExpenseRows);
                            const fTrf = filterTransfersByFYAndSearch(transfers);
                            const {cash, bank} = computeBalances(fInc, fExp, fTrf, opening.cash, opening.bank);
                            return <>Cash: {formatCurrency(cash)} | Bank: {formatCurrency(bank)}</>;
                          })()
                        }
                      </td>
                      <td colSpan={5}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {tab === "monthlySummary" && <MonthlySummaryReport token={token} finYear={finYear} />}
      {tab === "dailyJournal" && <DailyJournal token={token} finYear={finYear} />}
    </div>
  );
}